/**
 * QuickActions Component - Clean Design
 * Navigation cards for main features - matches Admin Panel style
 */
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Play, Timer, Sliders, Sparkles, Lock, BookOpen } from 'lucide-react';

const QuickActions = ({ user, hasPremiumAccess }) => {
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
      {/* Training Mode - Always available */}
      <button
        onClick={() => navigate('/training')}
        className="p-4 rounded-xl border-2 border-blue-500 bg-blue-50 dark:bg-blue-950/30 transition-all hover:scale-[1.02] hover:shadow-lg text-left"
        data-testid="training-mode-card"
      >
        <Play className="w-8 h-8 text-blue-600 mb-2" />
        <h3 className="font-semibold text-blue-700 dark:text-blue-300">Training Mode</h3>
        <p className="text-xs text-blue-600/70 dark:text-blue-400/70">Practice with feedback</p>
      </button>

      {/* Exam Simulation */}
      {(user?.is_guest || !hasPremiumAccess) ? (
        <button
          onClick={() => user?.is_guest ? navigate('/upgrade') : navigate('/store')}
          className="p-4 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 transition-all hover:scale-[1.02] text-left relative"
          data-testid="exam-mode-card-locked"
        >
          <Lock className="w-8 h-8 text-slate-400 mb-2" />
          <h3 className="font-semibold text-slate-500 dark:text-slate-400">Exam Simulation</h3>
          <p className="text-xs text-slate-400">
            {user?.is_guest ? 'Register to unlock' : 'Purchase to unlock'}
          </p>
          <Badge className="absolute top-2 right-2 bg-amber-500 text-white text-xs">PRO</Badge>
        </button>
      ) : (
        <button
          onClick={() => navigate('/exam')}
          className="p-4 rounded-xl border-2 border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 transition-all hover:scale-[1.02] hover:shadow-lg text-left"
          data-testid="exam-mode-card"
        >
          <Timer className="w-8 h-8 text-indigo-600 mb-2" />
          <h3 className="font-semibold text-indigo-700 dark:text-indigo-300">Exam Simulation</h3>
          <p className="text-xs text-indigo-600/70 dark:text-indigo-400/70">Real exam conditions</p>
        </button>
      )}

      {/* Custom Session */}
      {hasPremiumAccess ? (
        <button
          onClick={() => navigate('/custom')}
          className="p-4 rounded-xl border-2 border-purple-500 bg-purple-50 dark:bg-purple-950/30 transition-all hover:scale-[1.02] hover:shadow-lg text-left"
          data-testid="custom-session-card"
        >
          <Sliders className="w-8 h-8 text-purple-600 mb-2" />
          <h3 className="font-semibold text-purple-700 dark:text-purple-300">Custom Session</h3>
          <p className="text-xs text-purple-600/70 dark:text-purple-400/70">Configure practice</p>
        </button>
      ) : (
        <button
          onClick={() => user?.is_guest ? navigate('/upgrade') : navigate('/store')}
          className="p-4 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 transition-all hover:scale-[1.02] text-left relative"
          data-testid="custom-session-card-locked"
        >
          <Lock className="w-8 h-8 text-slate-400 mb-2" />
          <h3 className="font-semibold text-slate-500 dark:text-slate-400">Custom Session</h3>
          <p className="text-xs text-slate-400">
            {user?.is_guest ? 'Register to unlock' : 'Purchase to unlock'}
          </p>
          <Badge className="absolute top-2 right-2 bg-amber-500 text-white text-xs">PRO</Badge>
        </button>
      )}

      {/* Word Search - Always available */}
      <button
        onClick={() => navigate('/word-search')}
        className="p-4 rounded-xl border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 transition-all hover:scale-[1.02] hover:shadow-lg text-left"
        data-testid="word-search-card"
      >
        <Sparkles className="w-8 h-8 text-emerald-600 mb-2" />
        <h3 className="font-semibold text-emerald-700 dark:text-emerald-300">Word Search</h3>
        <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">Learn terms playfully</p>
      </button>

      {/* Glossary - Always available */}
      <button
        onClick={() => navigate('/glossary')}
        className="p-4 rounded-xl border-2 border-teal-500 bg-teal-50 dark:bg-teal-950/30 transition-all hover:scale-[1.02] hover:shadow-lg text-left"
        data-testid="glossary-card"
      >
        <BookOpen className="w-8 h-8 text-teal-600 mb-2" />
        <h3 className="font-semibold text-teal-700 dark:text-teal-300">Glossar</h3>
        <p className="text-xs text-teal-600/70 dark:text-teal-400/70">Begriffe & Definitionen</p>
      </button>
    </div>
  );
};

export default QuickActions;
